<template>
  <div class="variable-editor">
    <div class="ve-label caption grey--text text--darken-1">{{ label }}</div>
    <div
      ref="editor"
      class="ve-box"
      :class="{ 've-error': tooLong || (touched && required && !value), 've-disabled': disabled }"
      :style="{ minHeight: minHeight }"
      :contenteditable="disabled ? 'false' : 'true'"
      spellcheck="true"
      @input="onInput"
      @keydown.enter.prevent="onEnter"
      @paste.prevent="onPaste"
      @blur="onBlur"
      @keyup="saveRange"
      @mouseup="saveRange"
    ></div>
    <div class="d-flex caption mt-1" :class="tooLong ? 'error--text' : 'grey--text'">
      <span v-if="touched && required && !value" class="error--text">{{ label }} es obligatorio</span>
      <v-spacer></v-spacer>
      <span v-if="maxlength">{{ (value || '').length }}/{{ maxlength }}</span>
    </div>
  </div>
</template>

<script>
// Editor de texto plano donde las variables {{Nombre}} se ven como chips de un solo bloque.
// v-model = texto plano con los marcadores {{...}}; los saltos de línea van como \n.
const MARKER = /(\{\{[^}]+\}\})/

const escapeHtml = text => text
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')

export default {
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: 'Texto' },
    maxlength: { type: Number, default: 0 },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    minHeight: { type: String, default: '96px' }
  },

  data: () => ({
    touched: false,
    lastEmitted: null,
    savedRange: null
  }),

  computed: {
    tooLong () {
      return !!this.maxlength && (this.value || '').length > this.maxlength
    }
  },

  watch: {
    value (val) {
      // Solo se repinta si el cambio viene de fuera (no de lo que acaba de teclear el usuario)
      if (val !== this.lastEmitted) this.render(val)
    }
  },

  mounted () {
    this.render(this.value)
  },

  methods: {
    render (text) {
      const html = (text || '').split(MARKER).map(part => {
        if (MARKER.test(part)) {
          const name = part.slice(2, -2)
          return `<span class="ve-chip" contenteditable="false" data-marker="${escapeHtml(part).replace(/"/g, '&quot;')}">${escapeHtml(name)}</span>`
        }
        return escapeHtml(part)
      }).join('')
      this.$refs.editor.innerHTML = html
      this.lastEmitted = text || ''
    },

    // DOM -> texto plano con {{...}} y \n
    serialize () {
      const walk = (node, isFirst) => {
        if (node.nodeType === 3) return node.textContent
        if (node.nodeType !== 1) return ''
        if (node.dataset && node.dataset.marker) return node.dataset.marker
        if (node.tagName === 'BR') return '\n'
        const inner = Array.from(node.childNodes).map((c, i) => walk(c, i === 0)).join('')
        // Bloques que el navegador pueda crear (div/p): equivalen a un salto de línea
        return (node.tagName === 'DIV' || node.tagName === 'P') && !isFirst ? '\n' + inner : inner
      }
      return Array.from(this.$refs.editor.childNodes).map((c, i) => walk(c, i === 0)).join('')
    },

    onInput () {
      const text = this.serialize()
      this.lastEmitted = text
      this.$emit('input', text)
    },

    onBlur () {
      this.touched = true
      this.saveRange()
    },

    onEnter () {
      document.execCommand('insertText', false, '\n')
    },

    onPaste (e) {
      const text = (e.clipboardData || window.clipboardData).getData('text/plain')
      document.execCommand('insertText', false, text)
    },

    saveRange () {
      const sel = window.getSelection()
      if (sel && sel.rangeCount && this.$refs.editor.contains(sel.anchorNode)) {
        this.savedRange = sel.getRangeAt(0).cloneRange()
      }
    },

    // Inserta el chip de la variable en la posición del cursor (o al final si no hay cursor)
    insertVariable (marker) {
      const editor = this.$refs.editor
      editor.focus()
      const sel = window.getSelection()
      sel.removeAllRanges()
      if (this.savedRange && editor.contains(this.savedRange.startContainer)) {
        sel.addRange(this.savedRange)
      } else {
        const range = document.createRange()
        range.selectNodeContents(editor)
        range.collapse(false)
        sel.addRange(range)
      }
      const range = sel.getRangeAt(0)
      range.deleteContents()

      const chip = document.createElement('span')
      chip.className = 've-chip'
      chip.contentEditable = 'false'
      chip.dataset.marker = marker
      chip.textContent = marker.slice(2, -2)
      range.insertNode(chip)

      // Cursor justo después del chip
      const after = document.createRange()
      after.setStartAfter(chip)
      after.collapse(true)
      sel.removeAllRanges()
      sel.addRange(after)
      this.savedRange = after.cloneRange()
      this.onInput()
    }
  }
}
</script>

<style scoped>
.ve-box {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 8px 12px;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.7;
  font-size: 0.875rem;
  outline: none;
  background: #fff;
}
.ve-box:focus {
  border-color: var(--v-primary-base, #1976d2);
  box-shadow: 0 0 0 1px var(--v-primary-base, #1976d2);
}
.ve-error {
  border-color: #ff5252;
}
.ve-disabled {
  background: #f5f5f5;
  color: rgba(0, 0, 0, 0.5);
}
.ve-box >>> .ve-chip {
  display: inline-block;
  background-color: #fff3e0;
  color: #e65100;
  padding: 0 6px;
  margin: 0 1px;
  border-radius: 4px;
  font-size: 0.85em;
  border: 1px solid #ffe0b2;
  white-space: nowrap;
  user-select: all;
  cursor: default;
}
</style>
